import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.jsx'
import './metaPixel.js'
import './styles/index.css'

// Sentry: set VITE_SENTRY_DEV=1 to enable in dev (otherwise prod-only to avoid polluting stats).
const sentryEnabled =
  import.meta.env.VITE_SENTRY_DSN &&
  (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DEV === '1')
if (sentryEnabled) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'frontend-v2',
    tunnel: '/sentry-tunnel',
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /Can't find variable:\s*__firefox__/i,
      /window\.__firefox__\.reader/i,
      /undefined is not an object \(evaluating 'window\.__firefox__\.reader'\)/i,
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
    ],
    beforeSend(event) {
      const ex = event?.exception?.values?.[0]
      const value = ex?.value || ''
      const stackText =
        ex?.stacktrace?.frames
          ?.map((frame) => `${frame.filename || ''} ${frame.function || ''}`)
          .join(' ') || ''

      if (/__firefox__/i.test(value) || /__firefox__/i.test(stackText)) {
        return null
      }

      return event
    },
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  })
}

// PostHog analytics — initialise once at the app entry point.
// capture_pageview is false because we manually capture $pageview on route changes via App.jsx.
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || '/posthog-proxy',
    capture_pageview: false,
    capture_exceptions: true,
    // Anonymous visitors get person profiles (not only after identify); enables cohorts/lifecycle.
    person_profiles: 'always',
  })
}

// Mobile debugger — activate by visiting any page with ?eruda=1 in the URL.
// Loads Eruda from CDN so no build change is needed; safe to leave in production.
if (new URLSearchParams(window.location.search).get('eruda') === '1') {
  const s = document.createElement('script')
  s.src = 'https://cdn.jsdelivr.net/npm/eruda'
  s.onload = () => window.eruda.init()
  document.head.appendChild(s)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
